/**
 * `ServiceSuggestionList` — "what else for this trip": the unnumbered rows of
 * next services after a booking. One component for the chat's post-booking
 * card and the "Solicitação enviada" screen; each host decides what a row asks
 * for (`onSelect`).
 *
 * The outline groups the rows without becoming a second card on the page: the
 * same ring and radius as a chat card, no fill, with the radius CONCENTRIC to
 * the rows' (row radius + the padding). No shadow of its own: in the chat the
 * card sets the thread block elevation (`--block-elevation`, #6296), and the
 * "Solicitação enviada" screen sets nothing. The footer note talks about the
 * same options, so it lives inside the group.
 *
 * `disabled` freezes every row (a replayed or superseded chat card): the list
 * still says what was offered, but nothing can be asked again.
 *
 * The art is decorative (`alt=""`): the label next to it already names the
 * service. A row without art shows its `icon` in the same slot.
 */
import type { ReactNode } from 'react';
import { CaretRightIcon } from '@phosphor-icons/react';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';

interface ServiceSuggestion {
  readonly id: string;
  readonly label: string;
  /** A consultative fact after a `·` ("Grátis com seu TudoAzul"); never invented
   *  by the host. */
  readonly fact?: string;
  /** The service illustration URL. */
  readonly art?: string;
  /** The glyph when the service has no illustration. */
  readonly icon?: ReactNode;
  readonly recommended?: boolean;
}

interface ServiceSuggestionListCopy {
  readonly recommended: string;
  readonly note: string;
}

export interface ServiceSuggestionListProps {
  readonly suggestions: readonly ServiceSuggestion[];
  readonly copy: ServiceSuggestionListCopy;
  readonly onSelect: (id: string) => void;
  readonly disabled?: boolean;
  /** The heading that introduces the list, when the host shows one. */
  readonly labelledBy?: string;
}

export function ServiceSuggestionList({
  suggestions,
  copy,
  onSelect,
  disabled = false,
  labelledBy,
}: ServiceSuggestionListProps) {
  return (
    <fieldset
      aria-labelledby={labelledBy}
      className="border-border m-0 min-w-0 rounded-[calc(var(--radius-3xl)+--spacing(2))] border p-2 shadow-[var(--block-elevation,0_0_#0000)]"
    >
      <ol className="m-0 grid list-none gap-1 p-0">
        {suggestions.map((suggestion) => (
          <li key={suggestion.id}>
            <button
              type="button"
              disabled={disabled}
              className="text-title text-foreground enabled:hover:bg-muted flex w-full cursor-pointer items-center gap-3 rounded-3xl border-0 bg-transparent p-3 text-left disabled:cursor-default disabled:opacity-55"
              onClick={() => {
                onSelect(suggestion.id);
              }}
            >
              {suggestion.art ? (
                <img
                  src={suggestion.art}
                  alt=""
                  className="size-10 shrink-0 rounded-3xl object-cover"
                />
              ) : (
                <span
                  className="text-muted-foreground inline-flex size-10 shrink-0 items-center justify-center"
                  aria-hidden
                >
                  {suggestion.icon}
                </span>
              )}
              <span className="flex min-w-0 flex-wrap items-center gap-2 @max-[theme(--breakpoint-sm)]:flex-col @max-[theme(--breakpoint-sm)]:items-start @max-[theme(--breakpoint-sm)]:gap-1">
                <span>
                  {suggestion.label}
                  {suggestion.fact ? (
                    <span className="text-muted-foreground"> · {suggestion.fact}</span>
                  ) : null}
                </span>
                {suggestion.recommended ? (
                  <Badge variant="soft" color="neutral">
                    {copy.recommended}
                  </Badge>
                ) : null}
              </span>
              <span className="text-muted-foreground ml-auto inline-flex" aria-hidden>
                <CaretRightIcon size={16} aria-hidden />
              </span>
            </button>
          </li>
        ))}
      </ol>
      <p className="text-detail text-muted-foreground m-0 px-3 pt-1 pb-2">{copy.note}</p>
    </fieldset>
  );
}
